import { useState, useEffect } from "react";
import { Button, Table, Modal, Form, Dropdown } from "react-bootstrap";
import DashboardLayout from "../components/layout/DashboardLayout";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";

import { FaBox, FaPlus, FaEdit, FaTrash, FaDownload } from "react-icons/fa";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

interface Product {
  id_product: number;
  product_name: string;
  category: string;
  material: string;
  annual_capacity: number;
  unit: string;
}

export default function SupplierProducts() {
  const { translate } = useTranslate();
const t = translate;
  const [searchParams] = useSearchParams();
  const id_user =
    searchParams.get("id_user") ?? localStorage.getItem("userid") ?? "";

  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("product_name");

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [form, setForm] = useState({
    product_name: "",
    category: "",
    material: "",
    annual_capacity: "",
    unit: "",
  });
  const [loading, setLoading] = useState(false);
  const [colum, setSortColum] = useState("product_name");
  const [sort, setSort] = useState("ASC");

  const [selectedColumns, setSelectedColumns] = useState({
    product_name: true,
    category: true,
    material: true,
    annual_capacity: true,
    unit: true,
  });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  /* ================= LOAD ================= */

  async function loadProducts() {
    try {
      const res = await fetch(`${backendUrl}/api/supplier-products/${id_user}`);
      const data = await res.json();
      setProducts(data);
    } catch {
      toast.error(t("error_loading_products"));
    }
  }

  useEffect(() => {
    loadProducts();
  }, []);

  /* ================= SEARCH ================= */

  const filtered = products.filter((p) => {
    const value = (p as any)[searchField]?.toString().toLowerCase() || "";
    return value.includes(search.toLowerCase());
  });

  /* ================= PAGINATION ================= */

  const pageCount = Math.ceil(filtered.length / limit);
  const offset = page * limit;
  const current = filtered.slice(offset, offset + limit);

  /* ================= SORT ================= */

  const handleSortingColum = async (column: string) => {
    if (loading) return;

    const newSort = sort === "ASC" ? "DESC" : "ASC";

    setSortColum(column);
    setSort(newSort);
    setLoading(true);

    try {
      const res = await fetch(`${backendUrl}/api/supplier-products/${id_user}`);
      const data = await res.json();

      const sorted = [...data].sort((a: any, b: any) => {
        const aValue = a[column];
        const bValue = b[column];

        if (newSort === "ASC") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        }

        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      });

      setProducts(sorted);
    } catch (error) {
      console.error(error);
      toast.error(t("error_sorting"));
    } finally {
      setLoading(false);
    }
  };

  /* ================= SELECT ================= */

  const handleSelectAll = () => {
    if (selectedRows.length === current.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(current.map((p) => p.id_product));
    }
  };

  const handleSelectRow = (id: number) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((i) => i !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  /* ================= COLUMN TOGGLE ================= */

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prev: any) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  /* ================= EXPORT ================= */
  const selectedData = products.filter((p) =>
    selectedRows.includes(p.id_product),
  );

  function exportExcel() {
    if (selectedRows.length === 0) {
      toast.warning(t("select_one_product"));
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");

    XLSX.writeFile(wb, "products_selected.xlsx");
  }

  function exportCSV() {
    if (selectedRows.length === 0) {
      toast.warning("Select at least one product");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(selectedData);
    const csv = XLSX.utils.sheet_to_csv(ws);

    const blob = new Blob([csv]);
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "products_selected.csv";
    a.click();
  }
  function exportPDF() {
    if (selectedRows.length === 0) {
      toast.warning("Select at least one product");
      return;
    }

    const doc = new jsPDF();

    autoTable(doc, {
      head: [["Product", "Category", "Material", "Capacity", "Unit"]],
      body: selectedData.map((p) => [
        p.product_name,
        p.category,
        p.material,
        p.annual_capacity,
        p.unit,
      ]),
    });

    doc.save("products_selected.pdf");
  }

  /* ================= CRUD ================= */

  function resetForm() {
    setEditId(null);
    setForm({
      product_name: "",
      category: "",
      material: "",
      annual_capacity: "",
      unit: "",
    });
    setShowModal(false);
  }

  async function addProduct() {
    try {
      await fetch(`${backendUrl}/api/supplier-products/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_user,
          ...form,
          annual_capacity: Number(form.annual_capacity),
        }),
      });

      toast.success(t("product_added"));
      resetForm();
      loadProducts();
    } catch {
      toast.error(t("error_adding_product"));
    }
  }

  async function updateProduct() {
    try {
      await fetch(`${backendUrl}/api/supplier-products/update/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          annual_capacity: Number(form.annual_capacity),
        }),
      });

      toast.success(t("product_updated"));
      resetForm();
      loadProducts();
    } catch {
      toast.error(t("error_updating_product"));
    }
  }

  async function deleteProduct(id: number) {
    if (!window.confirm("Delete this product?")) return;

    await fetch(`${backendUrl}/api/supplier-products/${id}`, {
      method: "DELETE",
    });

    toast.success(t("product_deleted"));
    loadProducts();
  }

  /* ================= UI ================= */

  return (
    <div className="container-fluid">
      {/* HEADER */}
      <div className="row mb-3">
        <div className="col-md-6">
          <h4>
            <FaBox className="me-2" />
            {t("products")} ({products.length})
          </h4>
        </div>

        <div className="col-md-6 text-right">
          <Dropdown className="d-inline me-2">
            <Dropdown.Toggle variant="warning">
              <FaDownload /> {t("export")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={exportExcel}>Excel</Dropdown.Item>
              <Dropdown.Item onClick={exportCSV}>CSV</Dropdown.Item>
              <Dropdown.Item onClick={exportPDF}>PDF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="warning" onClick={() => setShowModal(true)}>
            <FaPlus /> {t("add_product")}
          </Button>
        </div>
      </div>

      {/* SEARCH + CONTROLS */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSearchField("product_name")}>
                  {t("product")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("category")}>
                  {t("category")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSearchField("material")}>
                  {t("material")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${t("search_by")} ${t(searchField)}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="secondary" onClick={() => setSearch("")}>
              ✕
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length d-flex align-items-center">
            <label style={{ marginBottom: "0" }}>
              {t("show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ms-2"
                style={{ width: "70px" }}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="">
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {Object.keys(selectedColumns).map((col) => (
                <Dropdown.Item key={col}>
                  <input
                    type="checkbox"
                    checked={
                      selectedColumns[col as keyof typeof selectedColumns]
                    }
                    onChange={() => handleColumnChange(col)}
                  />{" "}
                   {t(col)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* TABLE */}
      <Table className="dataTable role-table">
        <thead>
          <tr className="ligth ligth-data">
            <th style={{ width: "5%" }}>
              <div className="checkbox d-inline-block">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={selectedRows.length === current.length}
                  onChange={handleSelectAll}
                />
              </div>
            </th>

            {selectedColumns.product_name && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("product_name")}
              >
                {t("product")}
              </th>
            )}

            {selectedColumns.category && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("category")}
              >
                {t("category")}
              </th>
            )}

            {selectedColumns.material && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("material")}
              >
                {t("material")}
              </th>
            )}

            {selectedColumns.annual_capacity && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("annual_capacity")}
              >
                {t("capacity")}
              </th>
            )}

            {selectedColumns.unit && (
              <th
                className="sorting"
                onClick={() => handleSortingColum("unit")}
              >
                {t("unit")}
              </th>
            )}

            <th className="text-center">{t("actions")}</th>
          </tr>
        </thead>
        <tbody className="ligth-body">
          {loading ? (
            <tr>
              <td className="text-center" colSpan={7}>
                <p>
                  <PropagateLoader
                    color={"#123abc"}
                    loading={loading}
                    size={15}
                  />
                </p>
              </td>
            </tr>
          ) : current.length > 0 ? (
            current.map((p) => (
              <tr key={p.id_product}>
                <td>
                  <div className="checkbox d-inline-block">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={selectedRows.includes(p.id_product)}
                      onChange={() => handleSelectRow(p.id_product)}
                    />
                  </div>
                </td>

                {selectedColumns.product_name && <td>{p.product_name}</td>}
                {selectedColumns.category && <td>{p.category}</td>}
                {selectedColumns.material && <td>{p.material}</td>}
                {selectedColumns.annual_capacity && (
                  <td>{p.annual_capacity}</td>
                )}
                {selectedColumns.unit && <td>{p.unit}</td>}

                <td className="text-center">
                  <Button
                    size="sm"
                    variant="warning"
                    className="me-2 rounded-circle"
                    onClick={() => {
                      setEditId(p.id_product);
                      setForm({
                        product_name: p.product_name,
                        category: p.category,
                        material: p.material,
                        annual_capacity: String(p.annual_capacity),
                        unit: p.unit,
                      });
                      setShowModal(true);
                    }}
                  >
                    <FaEdit />
                  </Button>

                  <Button
                    size="sm"
                    variant="danger"
                    className="rounded-circle"
                    onClick={() => deleteProduct(p.id_product)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: "center" }}>
                {t("no_products")}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between mt-3">
        <span>
          {t("displaying")} {current.length} {t("out_of")} {filtered.length}
        </span>

        <ReactPaginate
          previousLabel={t("previous")}
          nextLabel={t("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPage(d.selected)}
          containerClassName={"pagination justify-content-end"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={page}
        />
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={resetForm}>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? t("edit_product") : t("add_product")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("product_name")}</Form.Label>
              <Form.Control
                value={form.product_name}
                onChange={(e) =>
                  setForm({ ...form, product_name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("category")}</Form.Label>
              <Form.Control
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("material")}</Form.Label>
              <Form.Control
                value={form.material}
                onChange={(e) => setForm({ ...form, material: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("annual_capacity")}</Form.Label>
              <Form.Control
                type="number"
                value={form.annual_capacity}
                onChange={(e) =>
                  setForm({ ...form, annual_capacity: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>{t("unit")}</Form.Label>
              <Form.Control
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>
            {t("cancel")}
          </Button>

          <Button
            variant="warning"
            onClick={editId ? updateProduct : addProduct}
          >
            {t("save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
